import WidgetView from '../widget-view'
import ImageConfig from './image-config'
import * as d3 from 'd3-selection'
import dimensions from './dimensions'
import FileSelector from './file-selector'
import readFile from 'scoreboard/components/read-file'

var ImageView = WidgetView.extend({
	className: 'widget image',
	resizable: true,
	lockedAR: true,
	configClass: ImageConfig,

	initialize: function () {
		WidgetView.prototype.initialize.apply(this, arguments)
		this.model.set('embeddedToken', this.canvas.embeddedToken)
		this.model.on('change:image', this._onImage.bind(this))

		// When an image is dropped onto the canvas, change:files is triggered.
		// Read the image as a data URL and set the 'image' property on the model,
		// which will cause the wob to be re-drawn and the image to appear
		this.model.on('change:files', () => {
			if (this.model.src()) {
				// #27532 there's already an image associated with this widget.
				// Probably just did an undo on a new image widget, so we can
				// ignore it
				return
			}

			let files = this.model.get('files')

			// #23084 after dropping an image, validate its file type.
			// FileSelector already does this, so take advantage of that
			if (this.fileSelector.validatePickedFile(files)) {
				return
			}

			readFile(files, (filename, dataUri) => {
				this.model.set({
					image: {
						dataUri: dataUri,
						name: filename
					}
				})
			})
		})
	},

	render: function () {
		WidgetView.prototype.render.apply(this, arguments)

		this._draw()

		this.container.on('moved', () => this.fileSelector?.removeErrorTooltip())

		return this
	},

	_draw: function (src) {
		let model = this.model
		src = src ?? this.model.src()

		this.$el.closest('.wob').toggleClass('no-image', !src)

		// always need the file selector to be available
		this._renderFileSelector()

		// If an image has not been chosen (ie doesn't have a src), and we're in
		// edit mode, and config is allowed (ie not a template copy), render the
		// controls for choosing an image
		if (!src && !this.readonly && this.allowConfig()) {
			this.readyForDisplayAndExport()
			return
		} else {
			this.fileSelector.$el.addClass('is-not-displayed')
		}

		var data = { src }

		var d3root = d3.select(this.el)
			.selectAll('img')
			.data([data])

		d3root.enter()
			.append('img')
			.merge(d3root)
			.on('load', function () {
				// Store the native image dimensions on the model. They'll be used for determining aspect ratio if the image is resized.
				model._naturalDimensions = {
					width: this.naturalWidth,
					height: this.naturalHeight
				}
			})
  			.attr('src', function (d) { return d.src })
  			.classed('is-not-displayed', function (d) { return !d.src })

		// the rendered image dimensions won't change after it's loaded, so we can tell the dashboard it's ready for display
		this.readyForDisplay()

		// once the actual image is loaded, it's ready for export
		this.$('img').on('load', this.readyForExport.bind(this))
	},

	_renderFileSelector: function () {
		// #23089 if a fileSelector was previously created, destroy it before creating
		// a new one
		this._removeFileSelector()

		this.fileSelector = new FileSelector()
		this.$el.append(this.fileSelector.el)
		this.fileSelector.render()

		this.listenToOnce(this.fileSelector, 'pick', image => {
			this.model.set('image', image)
		})
		this.listenToOnce(this.fileSelector, 'addSharedImage', metadata => {
			// Image was picked from shared images; set the ID and resize
			// the widget to fit the image
			this.model.properties.set('imageId', metadata.revisionId)
			this._initialFit(this.model.src())
		})
	},

	// Callback for when the 'image' property is set via the browser's filepicker
	// (giving us the dataUri); sets appropriate dimensions and re-draws the widget
	_onImage: function () {
		var image = this.model.get('image')
		if (!image) {
			return
		}

		this._initialFit(image.dataUri)
	},

	_initialFit(src) {
		this._removeFileSelector()

		// Change the wob's dimensions to match the image's natural
		// dimensions, but no larger than 1000px on either dimension
		dimensions(src, 1000, dim => {
			this.model.wob.resize(dim.width, dim.height, undefined, undefined, {
				ratio: dim.height / dim.width
			})
			this._draw(src)
		})
	},

	_removeFileSelector: function () {
		this.fileSelector?.remove()
		delete this.fileSelector
	},

	remove: function () {
		WidgetView.prototype.remove.apply(this, arguments)
		this._removeFileSelector()
	}
})

export default ImageView

ImageView.__filename__ = 'dashboards/widgets/image/image-view.js'
